import React, { useState } from 'react';
import {
  FileText,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  RotateCcw,
  ArrowUpRight,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { InvoiceDetailsDrawer } from '../components/sales/InvoiceDetailsDrawer';
import { useTransactions } from '../db';
import { useBaseCurrency } from '../db/hooks/useLookupData';
import { dayjs, formatPeriod } from '../lib/dayjs';

export function Sales() {
  const [period, setPeriod] = useState<string>(() => dayjs().format('YYYY-MM'));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);

  const { transactions = [], isLoading } = useTransactions({ type: 'income', period });
  const { currencySymbol, baseCurrency } = useBaseCurrency();

  const filteredTransactions = transactions.filter((tx) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (tx.customer_name && tx.customer_name.toLowerCase().includes(q)) ||
      (tx.description && tx.description.toLowerCase().includes(q)) ||
      (tx.id && tx.id.toLowerCase().includes(q))
    );
  });

  const handleOpenReceipt = (txId: string) => {
    setSelectedTxId(txId);
    setIsInvoiceDrawerOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      
      {/* ── Top Header Banner ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface border border-border p-6 rounded-2xl shadow-xs">
        <div>
          <div className="flex items-center gap-2 text-brand-orange font-bold text-xs uppercase tracking-wider mb-1">
            <FileText className="w-4 h-4 text-brand-orange" />
            Historial de Ventas & Facturación
          </div>
          <h1 className="text-2xl font-black text-text-primary">
            Ventas y Comprobantes Registrados
          </h1>
          <p className="text-xs text-text-secondary">
            Haz clic en cualquier transacción para abrir el Drawer de Detalle de Venta, comprobante e historial de auditoría.
          </p>
        </div>
      </div>

      {/* ── Search & Filter Controls ── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-secondary/40 border border-border p-4 rounded-2xl">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por cliente, concept o ID de venta..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-surface text-xs font-bold text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-4 h-4 text-text-tertiary" />
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 rounded-xl border border-border bg-surface text-xs font-bold text-text-primary focus:border-brand-sky"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => {
              const p = dayjs().subtract(i, 'month').format('YYYY-MM');
              return (
                <option key={p} value={p}>
                  {formatPeriod(p)}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* ── Sales Transactions List ── */}
      <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-xs divide-y divide-border/60">
        {isLoading && (
          <div className="p-12 text-center text-xs font-bold text-text-tertiary">
            Cargando historial de ventas...
          </div>
        )}

        {!isLoading && filteredTransactions.length === 0 && (
          <div className="p-12 text-center text-xs font-bold text-text-tertiary">
            No se encontraron ventas registradas en este período.
          </div>
        )}

        {!isLoading &&
          filteredTransactions.map((tx) => {
            const shortId = tx.id ? tx.id.substring(0, 8).toUpperCase() : '';
            const isCompleted = tx.payment_status === 'paid';
            const dateStr = dayjs(tx.date).format('DD MMM, YYYY • HH:mm');

            return (
              <div
                key={tx.id}
                onClick={() => handleOpenReceipt(tx.id)}
                className="p-4 hover:bg-surface-secondary/50 transition-colors flex items-center justify-between cursor-pointer group"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange font-bold text-xs">
                    #{shortId.substring(0, 4)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-extrabold text-sm text-text-primary group-hover:text-brand-orange transition-colors">
                        Venta #{shortId}
                      </h4>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-extrabold capitalize border ${
                          isCompleted
                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                        }`}
                      >
                        {tx.payment_status || 'paid'}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary">
                      {tx.customer_name || 'Cliente Mostrador'} • {tx.description}
                    </p>
                  </div>
                </div>

                <div className="text-right flex items-center gap-4">
                  <div>
                    <span className="font-black text-base text-text-primary block">
                      {currencySymbol}{(tx.total_amount || 0).toFixed(2)}
                    </span>
                    <span className="text-[10px] text-text-tertiary block font-mono">
                      {dateStr}
                    </span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-text-tertiary group-hover:text-brand-orange transition-colors" />
                </div>
              </div>
            );
          })}
      </div>

      {/* ── Integrated Invoice Details Drawer ── */}
      <InvoiceDetailsDrawer
        isOpen={isInvoiceDrawerOpen}
        onClose={() => setIsInvoiceDrawerOpen(false)}
        transactionId={selectedTxId}
      />
    </div>
  );
}
